// Mock data for CDL Academy redesign (frontend-only teaser)
// NOTE: All data here is mock/static. Checkout links redirect to app.cdlacademy.com.

import {
  Truck,
  Bus,
  FlaskConical,
  Users,
  ArrowUpRight,
  ShieldCheck,
  Headphones,
  BadgeCheck,
  DollarSign,
  Smartphone,
  ClipboardCheck,
} from "lucide-react";

export const COURSE_PRICE = 119.95;
export const ENDORSEMENT_PRICE = 59.95;

// Base (primary) courses — user picks ONE
export const baseCourses = [
  {
    id: "class-a",
    name: "ELDT Class A",
    tagline: "Tractor-Trailer / Big Rig",
    price: COURSE_PRICE,
    icon: Truck,
    description:
      "The full theory course for operating combination vehicles like tractor-trailers. Everything the FMCSA requires to sit for your Class A CDL skills test.",
    image: "/course-classa.webp",
    popular: true,
  },
  {
    id: "class-b",
    name: "ELDT Class B",
    tagline: "Box Trucks / Dump / Straight",
    price: COURSE_PRICE,
    icon: Truck,
    description:
      "Theory training for single heavy vehicles such as box trucks, dump trucks and buses. Required before your Class B CDL skills test.",
    image: "/course-classb.webp",
    popular: false,
  },
  {
    id: "class-b-to-a",
    name: "Class B to A Upgrade",
    tagline: "Upgrade Existing CDL",
    price: COURSE_PRICE,
    icon: ArrowUpRight,
    description:
      "Already hold a Class B CDL? This upgrade course covers the additional theory required to move up to a Class A license.",
    image: "/course-upgrade.webp",
    popular: false,
  },
];

// Optional endorsements — user can add MANY
export const endorsementCourses = [
  {
    id: "hazmat",
    name: "HazMat Endorsement",
    tagline: "Hazardous Materials",
    price: ENDORSEMENT_PRICE,
    icon: FlaskConical,
    description:
      "Theory training required to haul hazardous materials. Covers regulations, handling, and safety protocols.",
    image: "/course-hazmat.webp",
  },
  {
    id: "passenger",
    name: "Passenger Endorsement",
    tagline: "Transit & Coach",
    price: ENDORSEMENT_PRICE,
    icon: Users,
    description:
      "Required theory training for drivers transporting passengers in commercial vehicles.",
    image: "/course-passenger.webp",
  },
  {
    id: "school-bus",
    name: "School Bus Endorsement",
    tagline: "Student Transport",
    price: ENDORSEMENT_PRICE,
    icon: Bus,
    description:
      "Specialized theory training for the safe operation of a school bus and transport of students.",
    image: "/course-schoolbus.webp",
  },
];

export const allCourses = [...baseCourses, ...endorsementCourses];

export const stats = [
  { value: "100%", label: "Online & Self-Paced" },
  { value: "$119.95", label: "Per CDL License Class" },
  { value: "100+", label: "Years Instructor Experience" },
  { value: "Listed", label: "FMCSA Provider Registry" },
];

export const features = [
  {
    icon: DollarSign,
    title: "Affordable ELDT Training",
    text: "High-quality training for less. We choose public safety over high profits — CDL classes are $119.95 and endorsements just $59.95.",
  },
  {
    icon: BadgeCheck,
    title: "Built By Real Instructors",
    text: "Developed by a team of CDL instructors with over 100 years of combined real-world training experience.",
  },
  {
    icon: Headphones,
    title: "In-House Support Team",
    text: "Our support team is standing by to help you on the road to your goals. Have questions? Give us a call.",
  },
  {
    icon: ShieldCheck,
    title: "Listed On The FMCSA Registry",
    text: "We're listed as a provider on the FMCSA's Training Provider Registry, and the instant you finish we report your results to it — no waiting around.",
  },
  {
    icon: Smartphone,
    title: "100% Online — Any Device",
    text: "Every class is completed entirely online — no classroom, no in-person sessions. Learn on your phone, tablet, or laptop, anytime, at your own pace.",
  },
  {
    icon: ClipboardCheck,
    title: "Practice Test Included",
    text: "Every course includes a practice test so you walk into your CDL exam confident and prepared.",
  },
];

export const steps = [
  {
    number: "01",
    title: "Pick Your Course",
    text: "Choose your class and any endorsements you need. Classes are $119.95 and endorsements just $59.95.",
  },
  {
    number: "02",
    title: "Learn 100% Online",
    text: "Complete the FMCSA-required theory training entirely online — no classroom — on any device, at your own pace.",
  },
  {
    number: "03",
    title: "Pass The Practice Test",
    text: "Reinforce what you learned with our included practice test and get exam-ready.",
  },
  {
    number: "04",
    title: "We Report To FMCSA",
    text: "The moment you finish, we submit your results to the Training Provider Registry.",
  },
];

export const faqs = [
  {
    q: "What is ELDT and why do I need it?",
    a: "Entry-Level Driver Training (ELDT) is theory training mandated by the FMCSA. Anyone who obtained a commercial learner's permit (CLP) on or after February 7, 2022 must complete this training before taking their CDL skills or knowledge test.",
  },
  {
    q: "Are you listed with the FMCSA?",
    a: "Yes. CDL Academy is listed as a provider on the FMCSA's Training Provider Registry (TPR). The instant you complete a course, we report your results to the registry so testing centers can confirm your completion.",
  },
  {
    q: "How much does each course cost?",
    a: "Every CDL license class — Class A, Class B, and the Class B to A upgrade — is a flat $119.95. Each endorsement (HazMat, Passenger, and School Bus) is just $59.95. No hidden fees or upsells.",
  },
  {
    q: "Can I take the course on my phone?",
    a: "Absolutely. Our courses are fully mobile-friendly and work on any device — phone, tablet, or computer. Start on one device and pick up right where you left off on another.",
  },
  {
    q: "How long do the courses take?",
    a: "There is no minimum seat time for the theory portion under FMCSA rules. Most students complete a course at their own pace within a few sittings.",
  },
  {
    q: "What happens after I finish?",
    a: "As soon as you complete your training, we submit your results to the Training Provider Registry. CDL testing centers retrieve these results to confirm your completion before providing testing services.",
  },
];

export const testimonials = [
  {
    name: "Marcus T.",
    role: "Class A Driver",
    quote:
      "Straightforward, affordable, and I could do the whole thing on my phone during downtime. Passed my skills test the first try.",
  },
  {
    name: "Denise R.",
    role: "School Bus Driver",
    quote:
      "The school bus endorsement course was clear and easy to follow. Support answered my call right away when I had a question.",
  },
  {
    name: "Javier M.",
    role: "Class B to A Upgrade",
    quote:
      "I already had my Class B, so the upgrade course was perfect. $119.95 and results reported to FMCSA the same day. Can't beat it.",
  },
];

export const navLinks = [
  { label: "Home", to: "/" },
  { label: "Courses", to: "/courses" },
  { label: "Why ELDT", to: "/why-eldt" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const contactInfo = {
  phone: "(855) 696-0571",
  email: "support@cdlacademy.com",
  hours: "Mon–Fri, 8am – 6pm CST",
  address: "CDL Academy, United States",
};
